import { useEffect, useState } from 'react';
import { z } from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PortAutocomplete from '@/components/ports/PortAutocomplete';
import { SPECIALITES, type PortPro, uploadPortProLogo } from '@/hooks/usePortPros';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

const schema = z.object({
  port_id: z.string().uuid({ message: 'Sélectionnez un port' }),
  nom_entreprise: z.string().trim().min(1, 'Nom requis').max(120),
  specialite: z.string().min(1, 'Spécialité requise'),
  description: z.string().max(2000).optional().or(z.literal('')),
  adresse: z.string().max(200).optional().or(z.literal('')),
  code_postal: z.string().max(20).optional().or(z.literal('')),
  ville: z.string().max(100).optional().or(z.literal('')),
  telephone: z.string().max(40).optional().or(z.literal('')),
  email: z.string().email('Email invalide').max(255).optional().or(z.literal('')),
  site_web: z.string().url('URL invalide').max(255).optional().or(z.literal('')),
  horaires: z.string().max(500).optional().or(z.literal('')),
  logo_url: z.string().optional().or(z.literal('')),
  actif: z.boolean(),
  ordre_affichage: z.number().int().min(0).max(9999),
});

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  pro?: PortPro | null;
  onSubmit: (data: any) => Promise<void> | void;
}

const empty = {
  port_id: '',
  nom_entreprise: '',
  specialite: 'Shipchandler',
  description: '',
  adresse: '',
  code_postal: '',
  ville: '',
  telephone: '',
  email: '',
  site_web: '',
  horaires: '',
  logo_url: '',
  actif: true,
  ordre_affichage: 0,
};

export const PortProFormDialog = ({ open, onOpenChange, pro, onSubmit }: Props) => {
  const [form, setForm] = useState<any>(empty);
  const [portName, setPortName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (pro) {
        // Coerce nulls to '' to satisfy string schemas
        const cleaned: any = { ...empty };
        Object.keys(empty).forEach((k) => {
          const v = (pro as any)[k];
          cleaned[k] = v === null || v === undefined ? (empty as any)[k] : v;
        });
        setForm(cleaned);
        setPortName('');
      } else {
        setForm(empty);
        setPortName('');
      }
    }
  }, [open, pro]);

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const handleLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo trop volumineux (max 2 Mo)');
      return;
    }
    setUploading(true);
    try {
      const url = await uploadPortProLogo(file);
      set('logo_url', url);
      toast.success('Logo téléchargé');
    } catch (err: any) {
      toast.error(err.message || 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0].message);
      return;
    }
    setSubmitting(true);
    try {
      const payload: any = { ...parsed.data };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
      await onSubmit(pro ? { id: pro.id, ...payload } : payload);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{pro ? 'Modifier le professionnel' : 'Ajouter un professionnel'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Port rattaché *</Label>
            <PortAutocomplete
              value={portName}
              onChange={setPortName}
              onSelectPort={(p) => { set('port_id', p.id); setPortName(p.nom); }}
              placeholder="Rechercher un port…"
            />
            {form.port_id && (
              <p className="text-xs text-muted-foreground mt-1">Port sélectionné ✓</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Nom de l'entreprise *</Label>
              <Input value={form.nom_entreprise} onChange={(e) => set('nom_entreprise', e.target.value)} maxLength={120} />
            </div>
            <div>
              <Label>Spécialité *</Label>
              <Select value={form.specialite} onValueChange={(v) => set('specialite', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SPECIALITES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={3} maxLength={2000} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Adresse</Label>
              <Input value={form.adresse} onChange={(e) => set('adresse', e.target.value)} maxLength={200} />
            </div>
            <div>
              <Label>Code postal</Label>
              <Input value={form.code_postal} onChange={(e) => set('code_postal', e.target.value)} maxLength={20} />
            </div>
            <div>
              <Label>Ville</Label>
              <Input value={form.ville} onChange={(e) => set('ville', e.target.value)} maxLength={100} />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={form.telephone} onChange={(e) => set('telephone', e.target.value)} maxLength={40} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} maxLength={255} />
            </div>
            <div className="sm:col-span-2">
              <Label>Site web</Label>
              <Input type="url" placeholder="https://..." value={form.site_web} onChange={(e) => set('site_web', e.target.value)} maxLength={255} />
            </div>
          </div>

          <div>
            <Label>Horaires</Label>
            <Textarea value={form.horaires} onChange={(e) => set('horaires', e.target.value)} rows={2} maxLength={500} placeholder="Lun-Ven 9h-18h…" />
          </div>

          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex items-center gap-3 flex-wrap">
              {form.logo_url && (
                <img src={form.logo_url} alt="logo" className="h-14 w-14 rounded object-cover bg-muted" />
              )}
              <label className="inline-flex items-center gap-2 px-3 py-2 border rounded cursor-pointer hover:bg-muted text-sm">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {form.logo_url ? 'Remplacer (fichier)' : 'Téléverser un fichier'}
                <input type="file" accept="image/*" className="hidden" onChange={handleLogo} disabled={uploading} />
              </label>
              {form.logo_url && (
                <Button type="button" variant="ghost" size="sm" onClick={() => set('logo_url', '')}>Retirer</Button>
              )}
            </div>
            <Input
              type="url"
              placeholder="ou collez une URL d'image (https://...)"
              value={form.logo_url}
              onChange={(e) => set('logo_url', e.target.value)}
              maxLength={500}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 items-center">
            <div className="flex items-center gap-2">
              <Switch checked={form.actif} onCheckedChange={(v) => set('actif', v)} id="actif" />
              <Label htmlFor="actif">Actif</Label>
            </div>
            <div>
              <Label>Ordre d'affichage</Label>
              <Input
                type="number"
                min={0}
                value={form.ordre_affichage}
                onChange={(e) => set('ordre_affichage', parseInt(e.target.value || '0', 10))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={submitting || uploading}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {pro ? 'Enregistrer' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PortProFormDialog;
